import React, { useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-enterprise';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-balham.css';
import commonColDef from '../../../data/grid/commonColDef';
import colDef from '../../../data/grid/coldefManageDeletedCondition';
import useGridOptionsDeletedOrdinary from '../../../hooks/grid/gridOptions/useGridOptionsDeletedOrdinary';
import UndoDeletedCondition from '../../cellRenderers/UndoDeletedCondition';
import { useTranslation } from 'react-i18next';
import { CONDITION_VIEW_MODES } from '../../../constants';
import type { ColDef, GridApi, GridReadyEvent } from 'ag-grid-community';
import type { ConditionDetailType, ConditionViewMode, ConditionWithDetailTypeClient } from '../../../types/condition';

interface GridDeletedOrdinaryProps {
  isLoading: boolean;
  selectedViewMode: ConditionViewMode;
  condition: ConditionWithDetailTypeClient;
}

const GridDeletedOrdinary = ({ isLoading, condition, selectedViewMode }: GridDeletedOrdinaryProps): React.ReactElement => {
  const { t } = useTranslation();
  const [gridApi, setGridApi] = useState<GridApi | undefined>(undefined);
  const { gridOptions } = useGridOptionsDeletedOrdinary();
  const customColDef: ColDef = {
    flex: 1,
  };
  const defaultColDef: ColDef = Object.assign({}, commonColDef, customColDef);
  const frameworkComponents = {
    undoDeletedCondition: UndoDeletedCondition,
  };
  // 削除行に表示するデータを計算する。
  // ①基本的には承認済みの可視制御にはあって編集中の可視制御にはないものを表示する
  // ②ただし編集中の可視制御に存在するもの（削除した後に同じものを登録し直したケース）は除外する
  // ----------------------------------------
  // ①はcondition.detailOrdinaryApproveに存在してcondition.detailOrdinaryに存在しないものになる
  // ----------------------------------------
  const detailOrdinary: ConditionDetailType[] | undefined = condition?.detailOrdinary;
  const detailOrdinaryApprove: ConditionDetailType[] | undefined = condition?.detailOrdinaryApprove;
  const detailOrdinaryDiff: ConditionDetailType[] | undefined = detailOrdinaryApprove?.reduce((acc, cur) => {
    const id: number = cur.id;
    const foundOrdinary: ConditionDetailType | undefined = detailOrdinary.find((detail) => detail.approvedDetailId === id);
    if (foundOrdinary === undefined) {
      return [...acc, cur];
    } else {
      return acc;
    }
  }, []);
  // console.log('detailOrdinaryDiff', detailOrdinaryDiff, condition.detailOrdinary);
  // ①から編集中の可視制御に存在するものを除外する
  // ----------------------------------------
  const detailOrdinaryDiffFiltered: ConditionDetailType[] | undefined = detailOrdinaryDiff?.filter((ordinary) => {
    const sameDetailOrdinary: ConditionDetailType | undefined = condition.detailOrdinary.find((detail) => {
      if (
        // 可視制御7項目の値が同じものを探す
        // null/''の可能性がある　→　''に揃えて比較する
        (detail.customer ?? '') === (ordinary.customer ?? '') &&
        (detail.customer1 ?? '') === (ordinary.customer1 ?? '') &&
        (detail.customer2 ?? '') === (ordinary.customer2 ?? '') &&
        (detail.customer3 ?? '') === (ordinary.customer3 ?? '') &&
        (detail.deliveryLocation ?? '') === (ordinary.deliveryLocation ?? '') &&
        (detail.standard ?? '') === (ordinary.standard ?? '') &&
        (detail.sizeDisplay ?? '') === (ordinary.sizeDisplay ?? '')
      ) {
        return true;
      } else {
        return false;
      }
    });
    // 同じものがあったらフィルタ
    // console.log(sameDetailOrdinary);
    if (sameDetailOrdinary) {
      return false;
    } else {
      return true;
    }
  });
  // console.log('detailOrdinaryDiffFiltered', detailOrdinaryDiffFiltered);

  // SIDE EFFECT
  // ================================================================================

  // 確認用
  // useEffect(() => {
  // }, []);

  /**
   * Loading, NoRowsのグリッド表示
   */
  useEffect(() => {
    if (gridApi) {
      if (isLoading) {
        gridApi.showLoadingOverlay();
      } else {
        if (selectedViewMode === CONDITION_VIEW_MODES.EDITING && detailOrdinaryDiffFiltered?.length) {
          gridApi.hideOverlay();
        } else {
          gridApi.showNoRowsOverlay();
        }
      }
    } else {
      // do nothing
    }
  }, [gridApi, isLoading, selectedViewMode, condition]);

  // EVENT HANDLER
  // ================================================================================

  /**
   * グリッドが準備できたらAPIを利用できるようにする
   * @param params GridReadyEvent
   */
  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params.api);
  };

  // VIEW
  // ================================================================================

  return (
    <>
      <div id="deletedOrdinary" className="ag-theme-balham mt-4" style={{ height: '15vh' }}>
        <h5 style={{ fontSize: '1rem' }}>{t('condition.deletion')}</h5>
        <AgGridReact
          headerHeight={0}
          immutableData={true}
          gridOptions={gridOptions}
          defaultColDef={defaultColDef}
          columnDefs={colDef}
          rowData={detailOrdinaryDiffFiltered ?? []}
          onGridReady={onGridReady}
          frameworkComponents={frameworkComponents}
        />
      </div>
    </>
  );
};

export default GridDeletedOrdinary;
